import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import {
  collection, query, onSnapshot, doc, addDoc, updateDoc, writeBatch, serverTimestamp
} from 'firebase/firestore';
import { useRole } from './RoleContext';
import { CalendarDays, X, Trash2 } from 'lucide-react';
import { deleteToRecycleBin } from './recycleBinUtil';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const formatDate = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, '0')}-${d.toLocaleString('en-US', { month: 'short' })}-${d.getFullYear()}`;
};

const getWeekday = (iso) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '-' : WEEKDAYS[d.getDay()];
};

// Parses rows copied from a holiday list site (e.g. timeanddate.com) -- handles both
// tab-separated (when copy-pasting an HTML table keeps tabs) and multi-space-separated
// fallback text. Expected shape per line: "1 Jan   Thursday   New Year's Day   Optional Holiday"
// (weekday and type columns are optional).
// A real date cell looks like "1 Jan" or "21 Feb" -- anything else (a stray
// header row like "Date" that sometimes rides along with a copy-pasted table) is skipped.
const DATE_CELL = /^\d{1,2}\s+[A-Za-z]{3,9}$/;

const parseHolidayPaste = (text, year) => {
  const lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
  const results = [];
  const seen = new Set();
  for (const line of lines) {
    let parts = line.split('\t').map((s) => s.trim()).filter(Boolean);
    if (parts.length < 2) parts = line.split(/\s{2,}/).map((s) => s.trim()).filter(Boolean);
    if (parts.length < 2) continue;

    let type = '';
    if (/holiday|observance|event|festival/i.test(parts[parts.length - 1])) {
      type = parts.pop();
    }
    let dateStr = parts.shift();
    if (!DATE_CELL.test(dateStr)) continue;
    if (parts.length > 1 && WEEKDAYS.includes(parts[0])) parts.shift();
    const name = parts.join(' ').trim();
    if (!name) continue;

    const parsed = new Date(`${dateStr} ${year}`);
    if (isNaN(parsed.getTime())) continue;
    const iso = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;

    // The same holiday sometimes appears twice in a pasted list (e.g. an optional
    // religious observance shown alongside a government-declared version) -- keep one.
    const key = `${iso}|${name.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    results.push({ date: iso, name, type: type || 'Holiday' });
  }
  return results;
};

const TYPE_COLORS = {
  'Government Holiday': { bg: '#fce8e6', fg: '#c5221f' },
  'National Holiday': { bg: '#fde3cf', fg: '#b3540e' },
  'Optional Holiday': { bg: '#fff3cd', fg: '#8a6d3b' },
  'Observance': { bg: '#e3f2fd', fg: '#1565c0' },
  'Holiday': { bg: '#f4f7fe', fg: '#4a00e0' }
};
const typeColor = (type) => TYPE_COLORS[type] || TYPE_COLORS.Holiday;

const PublicHolidays = () => {
  const { isFullAccess: admin } = useRole();
  const [holidays, setHolidays] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());

  const [showBulk, setShowBulk] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);

  const [editingHoliday, setEditingHoliday] = useState(null);
  const [form, setForm] = useState({ date: '', name: '', type: 'Holiday' });
  const [showAdd, setShowAdd] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, 'publicHolidays')), (snap) => {
      setHolidays(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  const holidaysForYear = useMemo(
    () => holidays.filter((h) => (h.date || '').startsWith(String(year))).sort((a, b) => a.date.localeCompare(b.date)),
    [holidays, year]
  );

  const runPreview = () => setPreview(parseHolidayPaste(bulkText, year));

  const confirmImport = async () => {
    if (preview.length === 0 || importing) return;
    setImporting(true);
    try {
      // Re-importing for a year replaces what's already there instead of piling up
      // duplicates on top -- Sohan explicitly wants "paste again" to mean "start over".
      const batch = writeBatch(db);
      holidaysForYear.forEach((h) => batch.delete(doc(db, 'publicHolidays', h.id)));
      preview.forEach((h) => {
        const ref = doc(collection(db, 'publicHolidays'));
        batch.set(ref, { ...h, createdAt: serverTimestamp() });
      });
      await batch.commit();
      setShowBulk(false);
      setBulkText('');
      setPreview([]);
    } finally {
      setImporting(false);
    }
  };

  const openAdd = () => {
    setForm({ date: '', name: '', type: 'Holiday' });
    setShowAdd(true);
  };

  const openEdit = (h) => {
    setEditingHoliday(h);
    setForm({ date: h.date, name: h.name, type: h.type });
  };

  const saveHoliday = async () => {
    if (!form.date || !form.name.trim()) return alert('Please enter both a date and a name.');
    try {
      if (editingHoliday) {
        await updateDoc(doc(db, 'publicHolidays', editingHoliday.id), { date: form.date, name: form.name.trim(), type: form.type });
        setEditingHoliday(null);
      } else {
        await addDoc(collection(db, 'publicHolidays'), { date: form.date, name: form.name.trim(), type: form.type, createdAt: serverTimestamp() });
        setShowAdd(false);
      }
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  const deleteHoliday = async (h) => {
    if (!window.confirm(`Delete "${h.name}" (${formatDate(h.date)})?`)) return;
    await deleteToRecycleBin({ collectionName: 'publicHolidays', id: h.id, data: h, label: 'Public Holiday' });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <CalendarDays size={18} color="#4a00e0" />
          <h3 style={{ margin: 0 }}>Public Holidays — {year}</h3>
          <select className="input-field" value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ width: '110px', marginBottom: 0 }}>
            {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 1 + i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        {admin && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={openAdd} style={{ padding: '8px 16px', fontSize: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(45deg, #4a00e0, #8e2de2)', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>+ Add Holiday</button>
            <button onClick={() => setShowBulk(true)} style={{ padding: '8px 16px', fontSize: '12px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>📋 Bulk Paste Import</button>
          </div>
        )}
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
            <th style={{ padding: '12px' }}>Date</th>
            <th>Day</th>
            <th>Name</th>
            <th>Type</th>
            {admin && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {holidaysForYear.map((h) => (
            <tr key={h.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
              <td style={{ padding: '12px' }}>{formatDate(h.date)}</td>
              <td>{getWeekday(h.date)}</td>
              <td><b>{h.name}</b></td>
              <td>
                <span style={{ padding: '3px 9px', background: typeColor(h.type).bg, color: typeColor(h.type).fg, borderRadius: '8px', fontSize: '11px', fontWeight: 'bold' }}>{h.type}</span>
              </td>
              {admin && (
                <td>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => openEdit(h)} style={{ padding: '4px 10px', fontSize: '11px', borderRadius: '6px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => deleteHoliday(h)} style={{ padding: '4px 8px', borderRadius: '6px', border: 'none', background: '#ffeded', color: 'red', cursor: 'pointer' }}><Trash2 size={13} /></button>
                  </div>
                </td>
              )}
            </tr>
          ))}
          {holidaysForYear.length === 0 && (
            <tr><td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: '#999' }}>No holidays added for {year} yet.</td></tr>
          )}
        </tbody>
      </table>

      {showBulk && (
        <div onClick={() => setShowBulk(false)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '30px', borderRadius: '20px', width: '520px', maxWidth: '95%', maxHeight: '85vh', overflowY: 'auto', position: 'relative' }}>
            <button onClick={() => setShowBulk(false)} style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: '#ffeded', color: 'red', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer' }}><X size={16} /></button>
            <h3 style={{ marginTop: 0 }}>Bulk Import Holidays — {year}</h3>
            <p style={{ fontSize: '12px', color: '#888' }}>
              Copy the holiday table rows from a site like timeanddate.com and paste them below (one holiday per line). Make sure the Year above is set correctly first.
            </p>
            <textarea
              className="input-field" rows={8} placeholder={"1 Jan\tThursday\tNew Year's Day\tOptional Holiday\n21 Feb\tSaturday\tLanguage Martyrs' Day\tGovernment Holiday"}
              value={bulkText} onChange={(e) => setBulkText(e.target.value)}
              style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }}
            />
            <button onClick={runPreview} className="btn-primary" style={{ width: '100%', padding: '10px', marginTop: '5px' }}>Preview</button>

            {preview.length > 0 && (
              <div style={{ marginTop: '15px' }}>
                <b style={{ fontSize: '13px' }}>{preview.length} holiday(s) parsed:</b>
                <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '8px', border: '1px solid #eee', borderRadius: '10px' }}>
                  {preview.map((h, i) => (
                    <div key={i} className="hover-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 12px', fontSize: '12px', borderBottom: '1px solid #f9f9f9' }}>
                      <span>{formatDate(h.date)}</span><span>{h.name}</span><span style={{ color: '#888' }}>{h.type}</span>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: '11px', color: '#e74c3c', marginTop: '8px' }}>⚠️ This will replace all {holidaysForYear.length} existing {year} holiday(s) with this list.</p>
                <button onClick={confirmImport} disabled={importing} className="btn-confirm" style={{ width: '100%', padding: '12px', marginTop: '5px', opacity: importing ? 0.7 : 1 }}>
                  {importing ? 'Importing...' : `Import ${preview.length} Holiday(s)`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {(showAdd || editingHoliday) && (
        <div onClick={() => { setShowAdd(false); setEditingHoliday(null); }} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', padding: '30px', borderRadius: '20px', width: '360px', maxWidth: '95%', position: 'relative' }}>
            <button onClick={() => { setShowAdd(false); setEditingHoliday(null); }} style={{ position: 'absolute', top: '15px', right: '15px', border: 'none', background: '#ffeded', color: 'red', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer' }}><X size={16} /></button>
            <h3 style={{ marginTop: 0 }}>{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</h3>
            <input className="input-field" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            <input className="input-field" placeholder="Holiday Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <select className="input-field" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {['Holiday', 'Government Holiday', 'National Holiday', 'Optional Holiday', 'Observance'].map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <button onClick={saveHoliday} className="btn-primary" style={{ width: '100%', padding: '12px', marginTop: '5px' }}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicHolidays;
